import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Plus,
    PencilSimple,
    Copy,
    Trash,
    Play,
    Barbell,
} from '@phosphor-icons/react';
import { api, formatApiErrorDetail } from '@/lib/api';
import { timeAgo } from '@/lib/time';
import { WORKOUTS } from '@/constants/testIds';
import { useConfirm } from '@/components/ConfirmDialog';

export default function Workouts() {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState(null);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState(null);
    const { confirm, dialog: confirmDialog } = useConfirm();

    async function reload() {
        try {
            const { data } = await api.get('/templates');
            setTemplates(data);
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
            setTemplates([]);
        }
    }

    useEffect(() => {
        reload();
    }, []);

    async function duplicate(id) {
        setBusyId(id);
        setError('');
        try {
            await api.post(`/templates/${id}/duplicate`);
            await reload();
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
        } finally {
            setBusyId(null);
        }
    }

    async function remove(id) {
        const tpl = templates?.find((t) => t.id === id);
        const ok = await confirm({
            title: tpl ? `Delete "${tpl.name}"?` : 'Delete this template?',
            description:
                "The template will be gone, but any past sessions you've already logged from it stay in your history.",
            confirmLabel: 'Delete template',
            tone: 'danger',
        });
        if (!ok) return;
        setBusyId(id);
        setError('');
        try {
            await api.delete(`/templates/${id}`);
            setTemplates((prev) => prev.filter((t) => t.id !== id));
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
        } finally {
            setBusyId(null);
        }
    }

    async function start(template) {
        setBusyId(template.id);
        setError('');
        try {
            const { data } = await api.post(`/templates/${template.id}/start`);
            navigate(`/sessions/${data.id}`);
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
        } finally {
            setBusyId(null);
        }
    }

    const isLoading = templates === null;

    return (
        <main
            data-testid={WORKOUTS.root}
            className="mx-auto max-w-4xl px-5 sm:px-8 pt-10 sm:pt-14 pb-32"
        >
            <div className="flex items-end justify-between gap-3 animate-fade-up">
                <div>
                    <span className="uppercase-mono text-muted-foreground">
                        Library
                    </span>
                    <h1 className="mt-2 font-display font-extrabold tracking-tighter text-4xl sm:text-5xl">
                        Workout templates
                    </h1>
                    <p className="mt-3 text-muted-foreground max-w-xl">
                        Re-usable plans. Build once, start in one tap.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => navigate('/workouts/new')}
                    data-testid={WORKOUTS.newBtn}
                    className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.98] transition-all"
                >
                    <Plus size={18} weight="bold" />
                    <span className="hidden sm:inline">New template</span>
                    <span className="sm:hidden">New</span>
                </button>
            </div>

            {error && (
                <p className="mt-4 text-sm text-[#FF4D4D] font-medium">
                    {error}
                </p>
            )}

            {/* Body */}
            <section className="mt-10">
                {isLoading ? (
                    <p className="uppercase-mono text-muted-foreground animate-soft-pulse">
                        Loading…
                    </p>
                ) : templates.length === 0 ? (
                    <EmptyState onCreate={() => navigate('/workouts/new')} />
                ) : (
                    <ul
                        data-testid={WORKOUTS.list}
                        className="grid sm:grid-cols-2 gap-4"
                    >
                        {templates.map((t) => (
                            <TemplateCard
                                key={t.id}
                                template={t}
                                busy={busyId === t.id}
                                onStart={() => start(t)}
                                onDuplicate={() => duplicate(t.id)}
                                onDelete={() => remove(t.id)}
                            />
                        ))}
                    </ul>
                )}
            </section>
            {confirmDialog}
        </main>
    );
}

function TemplateCard({ template, busy, onStart, onDuplicate, onDelete }) {
    const exCount = (template.exercises || []).length;
    const totalSets = (template.exercises || []).reduce(
        (s, e) => s + (e.target_sets || 0),
        0
    );
    return (
        <li
            data-testid={`${WORKOUTS.item}-${template.id}`}
            className={`glass-card rounded-3xl p-5 sm:p-6 transition-all ${
                busy ? 'opacity-60' : 'hover:-translate-y-[2px]'
            }`}
        >
            <div className="flex items-start justify-between gap-3">
                <Link
                    to={`/workouts/${template.id}`}
                    data-testid={`${WORKOUTS.editLink}-${template.id}`}
                    className="min-w-0 flex-1 group"
                >
                    <span className="uppercase-mono text-muted-foreground">
                        Template
                    </span>
                    <h3 className="font-display font-extrabold tracking-tight text-2xl truncate mt-1 group-hover:underline underline-offset-4">
                        {template.name}
                    </h3>
                </Link>
                <span className="grid place-items-center w-11 h-11 rounded-2xl bg-foreground/5 border border-foreground/10 shrink-0">
                    <Barbell size={18} weight="bold" />
                </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
                <Stat label="Exercises" value={exCount} />
                <Stat label="Target sets" value={totalSets} />
            </div>

            {template.notes && (
                <p className="mt-4 text-sm text-muted-foreground line-clamp-2">
                    {template.notes}
                </p>
            )}

            <p className="mt-4 uppercase-mono text-muted-foreground">
                {template.last_performed_at ? (
                    <>
                        Last performed ·{' '}
                        <span className="text-foreground">
                            {timeAgo(template.last_performed_at)}
                        </span>
                    </>
                ) : (
                    'Never performed'
                )}
            </p>

            <div className="mt-5 flex items-center gap-2">
                <button
                    type="button"
                    onClick={onStart}
                    data-testid={`${WORKOUTS.startBtn}-${template.id}`}
                    disabled={busy || exCount === 0}
                    className="flex-1 h-11 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon active:scale-[0.98] transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                    <Play size={16} weight="fill" />
                    Start
                </button>
                <Link
                    to={`/workouts/${template.id}`}
                    data-testid={`${WORKOUTS.editLink}-${template.id}-icon`}
                    className="grid place-items-center w-11 h-11 rounded-2xl border border-foreground/10 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.96] transition-all"
                    aria-label="Edit template"
                >
                    <PencilSimple size={16} weight="bold" />
                </Link>
                <button
                    type="button"
                    onClick={onDuplicate}
                    data-testid={`${WORKOUTS.duplicateBtn}-${template.id}`}
                    disabled={busy}
                    className="grid place-items-center w-11 h-11 rounded-2xl border border-foreground/10 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.96] transition-all disabled:opacity-50"
                    aria-label="Duplicate template"
                >
                    <Copy size={16} weight="bold" />
                </button>
                <button
                    type="button"
                    onClick={onDelete}
                    data-testid={`${WORKOUTS.deleteBtn}-${template.id}`}
                    disabled={busy}
                    className="grid place-items-center w-11 h-11 rounded-2xl border border-foreground/10 bg-foreground/5 hover:bg-[#FF4D4D]/15 hover:border-[#FF4D4D]/40 hover:text-[#FF4D4D] active:scale-[0.96] transition-all disabled:opacity-50"
                    aria-label="Delete template"
                >
                    <Trash size={16} weight="bold" />
                </button>
            </div>
        </li>
    );
}

function Stat({ label, value }) {
    return (
        <div className="rounded-2xl border border-foreground/10 bg-foreground/[0.03] px-3 py-2.5">
            <p className="uppercase-mono text-muted-foreground mb-0.5">
                {label}
            </p>
            <p className="font-mono text-xl font-bold tabular-nums">
                {value}
            </p>
        </div>
    );
}

function EmptyState({ onCreate }) {
    return (
        <div
            data-testid={WORKOUTS.empty}
            className="glass-card grain rounded-3xl p-12 text-center"
        >
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-foreground/5 border border-foreground/10 mx-auto mb-5">
                <Barbell size={22} weight="bold" />
            </span>
            <h3 className="font-display font-extrabold tracking-tight text-2xl">
                No templates yet
            </h3>
            <p className="mt-2 text-muted-foreground max-w-md mx-auto">
                Save a workout plan once, then start it in a single tap from
                the gym floor.
            </p>
            <button
                type="button"
                onClick={onCreate}
                className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.98] transition-all"
            >
                <Plus size={18} weight="bold" />
                Create your first template
            </button>
        </div>
    );
}
